import { create } from 'zustand';
import { supabase, DbCampaignInvite } from '../lib/supabase';

/**
 * Campaign Invite Store
 *
 * Manages invite state for both DMs (creating/managing invites)
 * and players (viewing/accepting invites).
 */

// =============================================================================
// TYPES
// =============================================================================

export interface CampaignInvite {
  id: string;
  campaignId: string;
  code: string;
  email: string | null;
  status: 'pending' | 'accepted' | 'expired' | 'revoked';
  expiresAt: string | null;
  maxUses: number | null;
  useCount: number;
  createdAt: string;
}

export interface InvitePreview {
  valid: boolean;
  reason?: string;
  campaignId?: string;
  campaignName?: string;
  campaignDescription?: string | null;
  dmName?: string;
  playerCount?: number;
}

interface InviteState {
  // DM side: invites for their campaigns
  campaignInvites: CampaignInvite[];
  isLoadingInvites: boolean;
  invitesError: string | null;

  // Player side: invite preview
  invitePreview: InvitePreview | null;
  isLoadingPreview: boolean;
  previewError: string | null;

  // Accept state
  isAccepting: boolean;
  acceptError: string | null;

  // DM Actions
  loadCampaignInvites: (campaignId: string) => Promise<void>;
  createInvite: (
    campaignId: string,
    userId: string,
    options?: {
      expiresInHours?: number;
      maxUses?: number;
      email?: string;
    }
  ) => Promise<{ code: string; id: string } | null>;
  revokeInvite: (inviteId: string) => Promise<boolean>;

  // Player Actions
  loadInvitePreview: (code: string) => Promise<void>;
  acceptInvite: (code: string, userId: string) => Promise<{ success: boolean; campaignId?: string; alreadyMember?: boolean }>;
  clearPreview: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function dbToInvite(db: DbCampaignInvite): CampaignInvite {
  return {
    id: db.id,
    campaignId: db.campaign_id,
    code: db.code,
    email: db.email,
    status: db.status,
    expiresAt: db.expires_at,
    maxUses: db.max_uses,
    useCount: db.use_count,
    createdAt: db.created_at,
  };
}

/**
 * Generate invite code (8 chars, no ambiguous characters)
 */
function generateInviteCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars[Math.floor(Math.random() * chars.length)];
  }
  return code;
}

/**
 * Build invite URL for sharing
 */
export function getInviteUrl(code: string): string {
  const baseUrl = window.location.origin;
  return `${baseUrl}/#join/${code}`;
}

// =============================================================================
// STORE
// =============================================================================

export const useInviteStore = create<InviteState>()((set) => ({
  // Initial state
  campaignInvites: [],
  isLoadingInvites: false,
  invitesError: null,

  invitePreview: null,
  isLoadingPreview: false,
  previewError: null,

  isAccepting: false,
  acceptError: null,

  // =========================================================================
  // DM ACTIONS
  // =========================================================================

  loadCampaignInvites: async (campaignId: string) => {
    set({ isLoadingInvites: true, invitesError: null });

    try {
      const { data, error } = await supabase
        .from('machina_campaign_invites')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const invites = (data || []).map((d) => dbToInvite(d as DbCampaignInvite));
      set({ campaignInvites: invites, isLoadingInvites: false });
    } catch (err) {
      console.error('[InviteStore] Failed to load invites:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to load invites',
        isLoadingInvites: false,
      });
    }
  },

  createInvite: async (campaignId, userId, options = {}) => {
    set({ invitesError: null });

    try {
      const code = generateInviteCode();
      const expiresAt = options.expiresInHours
        ? new Date(Date.now() + options.expiresInHours * 60 * 60 * 1000).toISOString()
        : null;

      const { data, error } = await supabase
        .from('machina_campaign_invites')
        .insert({
          campaign_id: campaignId,
          code,
          email: options.email || null,
          created_by: userId,
          expires_at: expiresAt,
          max_uses: options.maxUses ?? 1,
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      const newInvite = dbToInvite(data as DbCampaignInvite);
      set((state) => ({
        campaignInvites: [newInvite, ...state.campaignInvites],
      }));

      return { code: newInvite.code, id: newInvite.id };
    } catch (err) {
      console.error('[InviteStore] Failed to create invite:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to create invite',
      });
      return null;
    }
  },

  revokeInvite: async (inviteId: string) => {
    set({ invitesError: null });

    try {
      const { error } = await supabase
        .from('machina_campaign_invites')
        .update({ status: 'revoked' })
        .eq('id', inviteId);

      if (error) throw error;

      // Update local state
      set((state) => ({
        campaignInvites: state.campaignInvites.map((inv) =>
          inv.id === inviteId ? { ...inv, status: 'revoked' as const } : inv
        ),
      }));

      return true;
    } catch (err) {
      console.error('[InviteStore] Failed to revoke invite:', err);
      set({
        invitesError: err instanceof Error ? err.message : 'Failed to revoke invite',
      });
      return false;
    }
  },

  // =========================================================================
  // PLAYER ACTIONS
  // =========================================================================

  loadInvitePreview: async (code: string) => {
    set({ isLoadingPreview: true, previewError: null, invitePreview: null });

    try {
      // Get invite
      const { data: invite, error: inviteError } = await supabase
        .from('machina_campaign_invites')
        .select('*')
        .eq('code', code.toUpperCase())
        .single();

      if (inviteError || !invite) {
        set({
          invitePreview: { valid: false, reason: 'Invite not found' },
          isLoadingPreview: false,
        });
        return;
      }

      const typedInvite = invite as DbCampaignInvite;

      // Check validity
      if (typedInvite.status === 'revoked') {
        set({
          invitePreview: { valid: false, reason: 'This invite has been revoked' },
          isLoadingPreview: false,
        });
        return;
      }

      if (typedInvite.expires_at && new Date(typedInvite.expires_at) < new Date()) {
        set({
          invitePreview: { valid: false, reason: 'This invite has expired' },
          isLoadingPreview: false,
        });
        return;
      }

      if (typedInvite.max_uses !== null && typedInvite.use_count >= typedInvite.max_uses) {
        set({
          invitePreview: { valid: false, reason: 'This invite has reached its maximum uses' },
          isLoadingPreview: false,
        });
        return;
      }

      // Get campaign details
      const { data: campaign, error: campaignError } = await supabase
        .from('machina_campaigns')
        .select('id, name, description, owner_id')
        .eq('id', typedInvite.campaign_id)
        .single();

      if (campaignError || !campaign) {
        set({
          invitePreview: { valid: false, reason: 'Campaign not found' },
          isLoadingPreview: false,
        });
        return;
      }

      // Get DM name
      const { data: owner } = await supabase
        .from('machina_users')
        .select('display_name')
        .eq('id', campaign.owner_id)
        .single();

      // Get player count
      const { count } = await supabase
        .from('machina_campaign_members')
        .select('*', { count: 'exact', head: true })
        .eq('campaign_id', campaign.id);

      set({
        invitePreview: {
          valid: true,
          campaignId: campaign.id,
          campaignName: campaign.name,
          campaignDescription: campaign.description,
          dmName: owner?.display_name || 'Unknown DM',
          playerCount: count || 0,
        },
        isLoadingPreview: false,
      });
    } catch (err) {
      console.error('[InviteStore] Failed to load invite preview:', err);
      set({
        previewError: err instanceof Error ? err.message : 'Failed to load invite',
        isLoadingPreview: false,
      });
    }
  },

  acceptInvite: async (code: string, userId: string) => {
    set({ isAccepting: true, acceptError: null });

    try {
      // Get invite
      const { data: invite, error: inviteError } = await supabase
        .from('machina_campaign_invites')
        .select('*')
        .eq('code', code.toUpperCase())
        .single();

      if (inviteError || !invite) {
        throw new Error('Invite not found');
      }

      const typedInvite = invite as DbCampaignInvite;

      // Check if already a member
      const { data: existingMember } = await supabase
        .from('machina_campaign_members')
        .select('campaign_id')
        .eq('campaign_id', typedInvite.campaign_id)
        .eq('user_id', userId)
        .single();

      if (existingMember) {
        set({ isAccepting: false });
        return { success: true, campaignId: typedInvite.campaign_id, alreadyMember: true };
      }

      // Add user to campaign
      const { error: memberError } = await supabase
        .from('machina_campaign_members')
        .insert({
          campaign_id: typedInvite.campaign_id,
          user_id: userId,
          role: 'player',
        });

      if (memberError) throw memberError;

      // Update invite use count
      const newUseCount = typedInvite.use_count + 1;
      const newStatus =
        typedInvite.max_uses !== null && newUseCount >= typedInvite.max_uses
          ? 'accepted'
          : 'pending';

      await supabase
        .from('machina_campaign_invites')
        .update({
          use_count: newUseCount,
          status: newStatus,
          accepted_at: new Date().toISOString(),
          accepted_by: userId,
        })
        .eq('id', typedInvite.id);

      set({ isAccepting: false });
      return { success: true, campaignId: typedInvite.campaign_id, alreadyMember: false };
    } catch (err) {
      console.error('[InviteStore] Failed to accept invite:', err);
      set({
        acceptError: err instanceof Error ? err.message : 'Failed to accept invite',
        isAccepting: false,
      });
      return { success: false };
    }
  },

  clearPreview: () => {
    set({
      invitePreview: null,
      isLoadingPreview: false,
      previewError: null,
      acceptError: null,
    });
  },
}));
